import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../../components/Flex";
import Input from "../../../../../../../../../../components/Input";
import Text from "../../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../../components/app/CustomTitle";
import CustomSelect from "../../../../../../../../../../components/CustomSelect";
import { Form2Payload } from "../../../../../../../scheduling/appointment/encounter/encounter-constant";
import CptCodesField from "../../components/form-components/CptCodesField";
import IcdCodesField from "../../components/form-components/IcdCodesField";
import { SelectedSectionsSummary } from "../../components/form-components/InitialPsychotherapy/SelectedFormSummary";

const EMDR_PHASE_OPTIONS = [
  { value: "history_taking", label: "Phase 1: History Taking" },
  { value: "preparation", label: "Phase 2: Preparation" },
  { value: "assessment", label: "Phase 3: Assessment" },
  { value: "desensitization", label: "Phase 4: Desensitization" },
  { value: "installation", label: "Phase 5: Installation" },
  { value: "body_scan", label: "Phase 6: Body Scan" },
  { value: "closure", label: "Phase 7: Closure" },
  { value: "reevaluation", label: "Phase 8: Reevaluation" },
];

const COPING_SKILLS_COLUMNS = [
  [
    { value: "grounding_techniques", label: "Grounding techniques" },
    { value: "safe_place_visualization", label: "Safe place visualization" },
    { value: "bilateral_stimulation", label: "Bilateral stimulation" },
    { value: "self_soothing_activities", label: "Self-soothing activities" },
  ],
  [
    { value: "breathing_exercises", label: "Breathing exercises" },
    { value: "container_exercise", label: "Container exercise" },
    { value: "mindfulness", label: "Mindfulness" },
    { value: "support_system_activation", label: "Support system activation" },
  ],
];

interface TraumaFocusedTherapyFormProps {
  formik: FormikProps<Form2Payload>;
}

const TraumaFocusedTherapyForm = ({
  formik,
}: TraumaFocusedTherapyFormProps) => {
  return (
    <>
      {/* Trauma & Safety Assessment */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Trauma & Safety Assessment</CustomTitle>
      </Flex>
      <Grid item xs={12}>
        <CustomTextArea
          label="Triggers Identified"
          rows={3}
          name="triggersIdentified"
          placeholder="Internal and external triggers discussed"
          onBlur={formik.handleBlur}
          value={formik.values?.triggersIdentified ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Dissociation Assessment"
          rows={3}
          name="dissociativeAssessment"
          placeholder="Level of dissociation, grounding needs"
          onBlur={formik.handleBlur}
          value={formik.values?.dissociativeAssessment ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Grounding Techniques Used"
          rows={3}
          name="groundingTechniquesUsed"
          placeholder="Specific grounding interventions employed"
          onBlur={formik.handleBlur}
          value={formik.values?.groundingTechniquesUsed ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Window of Tolerance Assessment"
          rows={3}
          name="windowOfToleranceAssessment"
          placeholder="Hyper/hypoarousal, capacity for processing trauma material"
          onBlur={formik.handleBlur}
          value={formik.values?.windowOfToleranceAssessment ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* EMDR Protocol */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>EMDR Protocol</CustomTitle>
      </Flex>
      <Grid item xs={12} md={6}>
        <Text mb={1}>EMDR Phase</Text>
        <CustomSelect
          name="emdrPhase"
          value={formik.values?.emdrPhase ?? ""}
          options={EMDR_PHASE_OPTIONS}
          placeholder="Select Phase"
          onChange={(v) => formik.setFieldValue("emdrPhase", v)}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Target Memory/Image"
          rows={3}
          name="targetMemoryImage"
          placeholder="Specific memory or image being processed"
          onBlur={formik.handleBlur}
          value={formik.values?.targetMemoryImage ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Negative Cognition (NC)",
            name: "negativeCognitionNc",
            placeholder: "e.g., 'I am powerless'",
            onBlur: formik.handleBlur,
            value: formik.values?.negativeCognitionNc ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Positive Cognition (PC)",
            name: "positiveCognitionPc",
            placeholder: "e.g., 'I am in control now'",
            onBlur: formik.handleBlur,
            value: formik.values?.positiveCognitionPc ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "VoC (Validity of Cognition) 1-7",
            name: "vocValidityOfCognition",
            placeholder: "-",
            onBlur: formik.handleBlur,
            value: formik.values?.vocValidityOfCognition ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Emotions",
            name: "emotions",
            placeholder: "Fear, shame, anger...",
            onBlur: formik.handleBlur,
            value: formik.values?.emotions ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "SUD (0-10)",
            name: "sudScale",
            placeholder: "-",
            onBlur: formik.handleBlur,
            value: formik.values?.sudScale ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Input
          textFieldProps={{
            fullWidth: true,
            label: "Body Sensations/Locations",
            name: "bodySensationsLocations",
            placeholder: "Where physical sensations are felt",
            onBlur: formik.handleBlur,
            value: formik.values?.bodySensationsLocations ?? "",
            onChange: formik.handleChange,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Desensitization (Sets & Processing)"
          rows={3}
          name="desensitizationSetsProcessing"
          placeholder="Number of sets, bilateral stimulation used, channels of association, SUD changes"
          onBlur={formik.handleBlur}
          value={formik.values?.desensitizationSetsProcessing ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Installation Process"
          rows={3}
          name="installationProcess"
          placeholder="Installation of positive cognition, final VOC score"
          onBlur={formik.handleBlur}
          value={formik.values?.installationProcess ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Body Scan Results"
          rows={3}
          name="bodyScanResults"
          placeholder="Residual tension or cleared sensations"
          onBlur={formik.handleBlur}
          value={formik.values?.bodyScanResults ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Closure"
          rows={3}
          name="closure"
          placeholder="Safe place, container exercise, debriefing"
          onBlur={formik.handleBlur}
          value={formik.values?.closure ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Trauma Processing Work */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Trauma Processing Work</CustomTitle>
      </Flex>
      <Grid item xs={12}>
        <CustomTextArea
          label="Trauma Narrative Work"
          rows={3}
          name="traumaNarrativeWork"
          placeholder="Narrative exposure, processing of traumatic memories"
          onBlur={formik.handleBlur}
          value={formik.values?.traumaNarrativeWork ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Cognitive Processing of Trauma"
          rows={3}
          name="cognitiveProcessingOfTrauma"
          placeholder="Stuck points, meaning-making, cognitive restructuring"
          onBlur={formik.handleBlur}
          value={formik.values?.cognitiveProcessingOfTrauma ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Somatic/Body-Based Interventions"
          rows={3}
          name="somaticBodyBasedInterventions"
          placeholder="Pendulation, titration, sensorimotor work, trauma release"
          onBlur={formik.handleBlur}
          value={formik.values?.somaticBodyBasedInterventions ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Resource Development & Stabilization */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Resource Development & Stabilization</CustomTitle>
      </Flex>
      <Grid item xs={12}>
        <CustomTextArea
          label="Resource Installation"
          rows={3}
          name="resourceInstallation"
          placeholder="Safe place, protective figures, positive resources developed"
          onBlur={formik.handleBlur}
          value={formik.values?.resourceInstallation ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <Text variant="body2" fontWeight={600} mb={1}>
          Coping Skills Practiced
        </Text>
        <Grid container spacing={1}>
          {COPING_SKILLS_COLUMNS.map((column) => (
            <Grid item xs={12} md={6} key={column[0].value}>
              <Flex flexDirection="column" gap={0.5}>
                {column.map((item) => {
                  const copingStr = formik.values?.copingSkillsPracticed ?? "";
                  const checkedKeys = copingStr
                    ? copingStr
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                    : [];
                  const isChecked = checkedKeys.includes(item.value);
                  return (
                    <FormControlLabel
                      key={item.value}
                      control={
                        <Checkbox
                          name={`copingSkillsPracticed.${item.value}`}
                          checked={isChecked}
                          onChange={(e) => {
                            const newKeys = e.target.checked
                              ? [...checkedKeys, item.value]
                              : checkedKeys.filter((k) => k !== item.value);
                            formik.setFieldValue(
                              "copingSkillsPracticed",
                              newKeys.join(",")
                            );
                          }}
                        />
                      }
                      label={<Text>{item.label}</Text>}
                    />
                  );
                })}
              </Flex>
            </Grid>
          ))}
        </Grid>
        <SelectedSectionsSummary
          formik={formik}
          showSectionLabel={false}
          sections={[
            {
              key: "copingSkillsPracticed",
              label: "Coping Skills Practiced",
              items: COPING_SKILLS_COLUMNS.flat().map((it) => ({
                name: it.value,
                label: it.label,
              })),
            },
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Stabilization Work"
          rows={3}
          name="stabilizationWork"
          placeholder="Affect regulation, distress tolerance, safety planning"
          onBlur={formik.handleBlur}
          value={formik.values?.stabilizationWork ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Safety Planning"
          rows={3}
          name="safetyPlanning"
          placeholder="Safety contacts, crisis plan, coping card"
          onBlur={formik.handleBlur}
          value={formik.values?.safetyPlanning ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Client Response & Progress */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Client Response & Progress</CustomTitle>
      </Flex>
      <Grid item xs={12}>
        <CustomTextArea
          label="Client Response to Interventions"
          rows={3}
          name="clientResponseToInterpretations"
          placeholder="Tolerance of trauma processing, engagement, affect regulation"
          onBlur={formik.handleBlur}
          value={formik.values?.clientResponseToInterpretations ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Progress Toward Trauma Treatment Goals"
          rows={3}
          name="progressTowardTraumaTreatmentGoals"
          placeholder="Reduction in PTSD symptoms, improved functioning"
          onBlur={formik.handleBlur}
          value={formik.values?.progressTowardTraumaTreatmentGoals ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Clinical Impression & Plan */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Clinical Impression & Plan</CustomTitle>
      </Flex>
      <Grid item xs={12}>
        <CustomTextArea
          label="Clinical Impression"
          rows={3}
          name="clinicalImpressions"
          placeholder="Overall clinical assessment and trauma formulation"
          onBlur={formik.handleBlur}
          value={formik.values?.clinicalImpressions ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Plan for Next Session"
          rows={3}
          name="planForNextSession"
          placeholder="Continue processing, stabilization work, or phase progression"
          onBlur={formik.handleBlur}
          value={formik.values?.planForNextSession ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          label="Risk Assessment"
          rows={3}
          name="riskAssessment"
          placeholder="Safety concerns, suicidal/homicidal ideation, self-harm assessment"
          onBlur={formik.handleBlur}
          value={formik.values?.riskAssessment ?? ""}
          onChange={formik.handleChange}
        />
      </Grid>

      {/* Clinical Diagnoses */}
      <Flex flexDirection="column" gap={1} ml={2} width="100%" mt={2}>
        <CustomTitle>Clinical Diagnoses</CustomTitle>
      </Flex>
      <Grid item xs={12}>
        <Flex flexDirection="row" gap={2} flexWrap="wrap">
          <IcdCodesField formik={formik} />
          <CptCodesField formik={formik} />
        </Flex>
      </Grid>

      {/* Psychoeducation Session Required — rendered by parent Psychotherapy.tsx to avoid duplication */}
      {/* <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              name="psychoeducationSessionRequired"
              checked={!!formik.values?.psychoeducationSessionRequired}
              onChange={(e) =>
                formik.setFieldValue(
                  "psychoeducationSessionRequired",
                  e.target.checked
                )
              }
            />
          }
          label={
            <Text variant="body1" fontWeight={500}>
              Psychoeducation Session Required?
            </Text>
          }
        />
      </Grid> */}
    </>
  );
};

export default TraumaFocusedTherapyForm;
